import React, { useContext, useMemo, useState } from "react";

import {
  Box,
  CircularProgress,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { updateUsersActiveStatus } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DisplayError from "components/shared/display-error";
import { subscriptionSeatTypesMapping } from "constants/seats";
import { SubscriptionSeatUsageContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

interface ActivateDeactivateSelectedUsersProps {
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  setError: (error: boolean) => void;
  setFailedUsers: (failedUsers: string[]) => void;
  userIds: string[];
  isPremiumOrg: boolean;
  usersCurrentStatus: UserStatus;
  handleRefreshOrgDisplay: () => void;
  handleRefreshUserList: () => void;
  // To be used when doing the one at a time activate/deactivate from the side menu
  // on the user record
  firstName?: string;
  lastName?: string;
}

const ActivateDeactivateUsersModal = ({
  open,
  setOpen,
  setError,
  setFailedUsers,
  userIds,
  isPremiumOrg,
  usersCurrentStatus,
  handleRefreshOrgDisplay,
  handleRefreshUserList,
  firstName,
  lastName,
}: ActivateDeactivateSelectedUsersProps) => {
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [seatType, setSeatType] = useState(
    SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
  );
  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { showError } = useContext(SnackbarContext);
  const userStatusToUpdateToStr = useMemo(() => {
    return usersCurrentStatus === UserStatus.ACTIVE ? "deactivate" : "activate";
  }, [usersCurrentStatus]);
  const unusedFullSeats = useMemo(() => {
    if (subscriptionSeatUsage?.premiumSeatUsageSummary?.unusedSeats) {
      return subscriptionSeatUsage.premiumSeatUsageSummary.unusedSeats;
    }
    return 0;
  }, [subscriptionSeatUsage]);

  const unusedLiteSeats = useMemo(() => {
    if (subscriptionSeatUsage?.liteSeatUsageSummary?.unusedSeats) {
      return subscriptionSeatUsage.liteSeatUsageSummary.unusedSeats;
    }
    return 0;
  }, [subscriptionSeatUsage]);

  const getTitleString = () => {
    if (userIds.length === 1) {
      return (
        userStatusToUpdateToStr[0].toUpperCase() +
        userStatusToUpdateToStr.substring(1) +
        " User"
      );
    }

    return (
      userStatusToUpdateToStr[0].toUpperCase() +
      userStatusToUpdateToStr.substring(1) +
      " Selected Users"
    );
  };

  const getConfirmBtnLabel = () => {
    if (userIds.length === 1) {
      return (
        userStatusToUpdateToStr[0].toUpperCase() +
        userStatusToUpdateToStr.substring(1) +
        " User"
      );
    }

    return (
      userStatusToUpdateToStr[0].toUpperCase() +
      userStatusToUpdateToStr.substring(1) +
      " Users"
    );
  };

  const handleSeatTypeChange = (
    event: SelectChangeEvent<SubscriptionSeatTypes>
  ) => {
    setSeatType(event.target.value as number);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await updateUsersActiveStatus(
      {
        userIds,
        orgId: roleId,
        // If active, set to false as we are deactivating them
        active: usersCurrentStatus === UserStatus.ACTIVE ? false : true,
        seatType: isPremiumOrg
          ? seatType
          : SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        if (res.failedUsers.length > 0) {
          setError(true);
          setFailedUsers(res.failedUsers);
        } else {
          handleRefreshOrgDisplay();
          handleRefreshUserList();
        }
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  let notEnoughSeatsError = "";
  if (isPremiumOrg) {
    if (
      seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM &&
      unusedFullSeats < userIds.length
    ) {
      notEnoughSeatsError =
        "Number activating must be less than or equal to the number of full seats available." +
        " Try again with fewer users activating or increase full seats first then try again.";
    }
    if (
      seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE &&
      unusedLiteSeats < userIds.length
    ) {
      notEnoughSeatsError =
        "Number activating must be less than or equal to the number of lite seats available." +
        " Try again with fewer users activating or increase lite seats first then try again.";
    }
  }

  return (
    <>
      <ConfirmDialog
        title={getTitleString()}
        disableBackgroundClick
        cancelBtnLabel="Cancel"
        confirmBtnLabel={getConfirmBtnLabel()}
        open={open}
        setOpen={setOpen}
        onConfirm={async () => {
          await handleSubmit();
          setOpen(false);
        }}
        onCancel={() => {
          setSeatType(
            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
          );
        }}
        disableCloseOnConfirm
        disabled={notEnoughSeatsError !== ""}
      >
        <DialogContent
          style={{ paddingBottom: "0" }}
          data-testid="activate-deactivate-users-modal-content"
        >
          <Grid container direction="row" spacing={1}>
            <Grid item xs={12} container direction="column">
              {/* For when activating/deactivating from individual side menu */}
              {firstName && lastName ? (
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <p data-testid="activate-deactivate-first-last-name">
                      You are about to {userStatusToUpdateToStr} the user{" "}
                      {`"${firstName} ${lastName}"`}.
                    </p>
                  </Grid>
                </Grid>
              ) : (
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <p data-testid="activate-deactivate-standard-message">
                      You are about to {userStatusToUpdateToStr}{" "}
                      {userIds.length} user
                      {userIds.length !== 1 && "s"}.{" "}
                    </p>
                  </Grid>
                </Grid>
              )}
              {notEnoughSeatsError && (
                <div className={clsx(classes.spaceBottom)}>
                  <DisplayError
                    errorMessage={notEnoughSeatsError}
                    align="left"
                  />
                </div>
              )}
              {/* Only show seat type select when activating on premium */}
              {isPremiumOrg && usersCurrentStatus !== UserStatus.ACTIVE && (
                <Grid container alignItems="center">
                  <form
                    className={clsx(classes.spaceTopSmall)}
                    noValidate
                    autoComplete="off"
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      className={clsx(classes.formControl)}
                    >
                      <InputLabel id="select-seat-type-label">
                        Seat Type
                      </InputLabel>
                      <Select
                        labelId="select-seat-type-label"
                        id="select-seat-type"
                        value={seatType}
                        label="Seat Type"
                        onChange={handleSeatTypeChange}
                        data-testid="seat-type-select"
                        inputProps={{
                          "data-testid": "seat-type-select-input",
                        }}
                      >
                        <MenuItem
                          value={
                            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM
                          }
                          data-testid="seat-type-full-option"
                        >
                          {
                            subscriptionSeatTypesMapping[
                              SubscriptionSeatTypes
                                .SUBSCRIPTION_SEAT_TYPE_PREMIUM
                            ]
                          }
                        </MenuItem>
                        <MenuItem
                          value={
                            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                          }
                          data-testid="seat-type-free-option"
                        >
                          {
                            subscriptionSeatTypesMapping[
                              SubscriptionSeatTypes
                                .SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                            ]
                          }
                        </MenuItem>
                        <MenuItem
                          value={
                            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE
                          }
                          data-testid="seat-type-lite-option"
                        >
                          {
                            subscriptionSeatTypesMapping[
                              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE
                            ]
                          }
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </form>
                </Grid>
              )}
            </Grid>
          </Grid>
          {isLoading && (
            <Grid
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1.5rem",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                }}
              >
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={4}
                  size={50}
                />
              </Box>
            </Grid>
          )}
        </DialogContent>
      </ConfirmDialog>
    </>
  );
};

export default ActivateDeactivateUsersModal;
